import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useUserAuth } from "context/UserContext";
import { studioApi } from "services/studioService";
import StudioMeter, { planBannerText, planStatusLabel } from "./StudioMeter";
import {
  PLAN_TERMS_VERSION,
  contractLang,
  errorText,
  fixedT,
  formatDay,
  formatPlanPrice,
  localeFor,
  priceLine,
  pricePhrase,
  siteLang,
  tierLabel,
} from "./studioText";
import "pages/studio/studio.css";

/* The Studio plan in Account settings → Subscription (docs/studio-plans-spec.md,
   W-7). It reads POST /studio/plan on the main backend, so it works without
   Studio access: a client can always see, change and cancel what charges them.

   - Facts: plan, status, price and next charge, how it shows on the
     statement, country, the renewal reminder.
   - Usage: the meters, live apps, free repairs, the reset date.
   - Change: upgrade (the prorated amount now, the extra messages, then the new
     monthly price; an unticked agree box) or downgrade at renewal (which app
     stays live). Undo a scheduled downgrade.
   - Cancel (focus moves to the question) and resume; Manage billing opens the
     Stripe portal of the Studio customer.

   Plan state goes to the one role="status" line; errors from a button go to
   a role="alert" box. */

const PAID = new Set(["active", "trialing"]);
const PAST_DUE = new Set(["past_due", "unpaid"]);
const ENDED = new Set(["canceled", "incomplete_expired"]);
const STARTER_LIVE_APPS = 1;
const TITLE_ID = "studio-plan-panel-title";

function StudioPlanPanel() {
  const { t, i18n } = useTranslation();
  const { user } = useUserAuth();
  const lang = siteLang(i18n);
  const invited = user?.studioEnabled === true;

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(null);
  const [change, setChange] = useState(null); // { direction, preview, agreed, keep }
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const cancelQuestionRef = useRef(null);
  const changeHeadingRef = useRef(null);

  const userRef = useRef(user);
  userRef.current = user;

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      setData(await studioApi.plan(userRef.current));
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (confirmingCancel) cancelQuestionRef.current?.focus();
  }, [confirmingCancel]);

  // Focus the change form's heading when it opens.
  const changeDirection = change?.direction;
  useEffect(() => {
    if (changeDirection) changeHeadingRef.current?.focus();
  }, [changeDirection]);

  const plan = data?.plan || null;
  const billing = data?.billing || null;

  // Nothing to show to someone with no Studio plan and no invite (course-only
  // accounts), not even while it loads.
  if (!invited && (!data || loadError || (!billing && (!plan || plan.status === "none")))) return null;

  // The contract language of a change follows the plan's country.
  const country = billing?.country || "US";
  const cLang = contractLang(lang, country);
  const ct = fixedT(i18n, t, cLang);
  const cLocale = localeFor(cLang, country);
  const locale = localeFor(lang, country);

  const updated = (res, message) => {
    setData((d) => ({ ...d, ...res }));
    setStatus(message);
  };

  const fail = (err) => {
    const link = err?.code === "payment_action_required" ? err.details?.hostedInvoiceUrl : null;
    setError({ text: errorText(t, err), link });
  };

  const run = async (name, fn) => {
    setWorking(name);
    setError(null);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setWorking(null);
    }
  };

  const openChange = (toTier) =>
    run("preview", async () => {
      const preview = await studioApi.planChangePreview(user, toTier);
      setChange({ toTier, direction: preview.direction, preview, agreed: false, keep: null, stale: null });
    });

  const changeTexts = () => {
    const p = change.preview;
    // The new monthly price always carries its tax wording (plus sales tax,
    // IVA incluido, precio total), like the checkout and the backend's own
    // text of the change, so what the customer agrees to here says the same.
    if (change.direction === "up") {
      const amount = formatPlanPrice({ amount: p.amountDue, currency: p.currency }, cLocale);
      const taxNote = !p.taxIncluded && p.newPrice?.tax === "exclusive" ? ct("studio.plan.change.taxNote") : "";
      const agreeArgs = { amount, taxNote, newPrice: formatPlanPrice(p.newPrice, cLocale) };
      return {
        text: ct("studio.plan.change.upgradeText", {
          amount,
          taxNote,
          bonus: p.bonusMessages ?? 0,
          newPrice: pricePhrase(ct, p.newPrice, cLocale),
          renewsAt: formatDay(p.renewsAt, cLocale),
        }),
        agree:
          p.newPrice?.tax === "inclusive"
            ? ct("studio.plan.change.agreeUpgradeMX", agreeArgs)
            : p.newPrice?.tax === "none"
              ? ct("studio.plan.change.agreeUpgradeCR", agreeArgs)
              : ct("studio.plan.change.agreeUpgradeUS", agreeArgs),
      };
    }
    const live = p.liveProjects || [];
    const keep = live.length > STARTER_LIVE_APPS ? change.keep : live.map((x) => x.id)[0] ?? null;
    const paused = live.filter((x) => x.id !== keep).map((x) => x.name);
    const args = { date: formatDay(p.effectiveAt, cLocale), price: pricePhrase(ct, p.newPrice, cLocale) };
    return {
      text: paused.length && keep
        ? ct("studio.plan.change.downgradeText", { ...args, apps: paused.join(", "), count: paused.length })
        : ct("studio.plan.change.downgradeTextNoApps", args),
    };
  };

  const confirmChange = () => {
    const p = change.preview;
    const up = change.direction === "up";
    const live = p.liveProjects || [];
    const needsKeep = !up && live.length > STARTER_LIVE_APPS;
    if ((up && !change.agreed) || (needsKeep && !change.keep)) return;
    const { text, agree } = changeTexts();
    return run("change", async () => {
      try {
        const res = await studioApi.planChange(user, {
          toTier: change.toTier,
          ...(needsKeep ? { keepProjectIds: [change.keep] } : {}),
          acceptedTerms: true,
          termsVersion: PLAN_TERMS_VERSION,
          termsText: [text, agree].filter(Boolean).join("\n"),
          ...(up ? { prorationDate: p.prorationDate, previewAmount: p.amountDue } : {}),
          locale: cLang,
        });
        setChange(null);
        updated(
          res,
          up
            ? t("studio.plan.change.upgraded")
            : t("studio.plan.change.scheduled", { date: formatDay(res?.billing?.pendingChange?.effectiveAt || p.effectiveAt, locale) })
        );
      } catch (err) {
        if (err?.code === "change_preview_stale") {
          // The amount moved: show the new one, ask again, and say so.
          const preview = await studioApi.planChangePreview(user, change.toTier);
          const amount = formatPlanPrice({ amount: preview.amountDue, currency: preview.currency }, locale);
          setChange((c) => ({ ...c, preview, agreed: false }));
          setError({ text: t("studio.plan.change.stale", { amount }) });
          return;
        }
        throw err;
      }
    });
  };

  const undoChange = () =>
    run("undo", async () => updated(await studioApi.planDowngradeCancel(user), t("studio.plan.change.undone")));

  const cancelPlan = () =>
    run("cancel", async () => {
      const res = await studioApi.planCancel(user);
      setConfirmingCancel(false);
      updated(res, t("studio.plan.canceled"));
    });

  const resumePlan = () => run("resume", async () => updated(await studioApi.planResume(user), t("studio.plan.resumed")));

  const portal = () =>
    run("portal", async () => {
      window.location.assign(await studioApi.planPortal(user));
    });

  const statusRegion = <div role="status" className={status ? "studio-notice" : "sr-only"}>{status && <p>{status}</p>}</div>;
  const errorBox = error && (
    <div className="studio-notice" role="alert">
      <p>
        {error.text}{" "}
        {error.link && (
          <a href={error.link} target="_blank" rel="noopener noreferrer">{t("studio.plan.change.finishPayment")}</a>
        )}
      </p>
    </div>
  );

  if (loadError) {
    return (
      <section className="studio studio-plan-panel" aria-labelledby={TITLE_ID}>
        <h3 id={TITLE_ID}>{t("studio.plan.title")}</h3>
        <div className="studio-notice" role="alert">
          <p>{t("studio.plan.loadError")}</p>
          <button type="button" className="studio-btn studio-btn-small" onClick={load}>{t("studio.plan.retry")}</button>
        </div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="studio studio-plan-panel" aria-labelledby={TITLE_ID}>
        <h3 id={TITLE_ID}>{t("studio.plan.title")}</h3>
        <p className="studio-muted studio-loading">{t("studio.plan.loading")}</p>
      </section>
    );
  }

  const hasBilling = Boolean(billing) && !ENDED.has(billing.status);
  if (!hasBilling && (!plan || plan.status === "none")) {
    return (
      <section className="studio studio-plan-panel" aria-labelledby={TITLE_ID}>
        <h3 id={TITLE_ID}>{t("studio.plan.title")}</h3>
        {statusRegion}
        <p>
          {t("studio.plan.none")} <Link to="/studio/plans">{t("studio.plan.seePlans")}</Link>
        </p>
      </section>
    );
  }

  const paid = hasBilling && PAID.has(billing.status);
  const pastDue = hasBilling && PAST_DUE.has(billing.status);
  const ending = paid && billing.cancelAtPeriodEnd;
  const banner = planBannerText(t, plan, billing, locale);
  const canChange =
    paid && !ending && !billing.pendingChange && plan?.source === "stripe" && plan.status === "active" && !change;
  const otherTier = plan?.tier === "starter" ? "business" : plan?.tier === "business" ? "starter" : null;

  const changeForm = () => {
    if (!change) return null;
    const up = change.direction === "up";
    const live = change.preview.liveProjects || [];
    const needsKeep = !up && live.length > STARTER_LIVE_APPS;
    const { text, agree } = changeTexts();
    const missing = up ? !change.agreed : needsKeep && !change.keep;
    const hintId = "studio-plan-change-hint";
    return (
      <div className="studio-plan-change">
        <h4 ref={changeHeadingRef} tabIndex={-1}>
          {up ? t("studio.plan.change.upgradeTo") : t("studio.plan.change.downgradeTo")}
        </h4>
        <div lang={cLang}>
          {needsKeep && (
            <fieldset className="studio-fieldset">
              <legend>{ct("studio.plan.change.keepLegend")}</legend>
              {live.map((app) => (
                <label key={app.id} className="studio-radio">
                  <input
                    type="radio"
                    name="studio-plan-keep"
                    value={app.id}
                    checked={change.keep === app.id}
                    onChange={() => setChange((c) => ({ ...c, keep: app.id }))}
                    disabled={working !== null}
                  />
                  <span>{app.name}</span>
                </label>
              ))}
              <p className="studio-muted">{ct("studio.plan.change.keepHint")}</p>
            </fieldset>
          )}
          <p className="studio-plan-change__text">{text}</p>
          {up && (
            <label className="cn-legal-check" htmlFor="studio-plan-change-agree">
              <input
                id="studio-plan-change-agree"
                type="checkbox"
                checked={change.agreed}
                onChange={(e) => setChange((c) => ({ ...c, agreed: e.target.checked }))}
                disabled={working !== null}
              />
              <span>{agree}</span>
            </label>
          )}
        </div>
        <div className="studio-plan-panel__actions">
          <button
            type="button"
            className="studio-btn studio-btn-primary"
            onClick={confirmChange}
            aria-disabled={missing || working !== null ? "true" : undefined}
            aria-describedby={missing ? hintId : undefined}
          >
            {working === "change" ? t("studio.plan.working") : up ? t("studio.plan.change.confirmUpgrade") : t("studio.plan.change.confirmDowngrade")}
          </button>
          <button type="button" className="studio-btn" onClick={() => { setChange(null); setError(null); }} disabled={working !== null}>
            {t("studio.plan.change.back")}
          </button>
        </div>
        {missing && (
          <p id={hintId} className="studio-muted">
            {up ? t("studio.plan.change.hintAgree") : t("studio.plan.change.hintKeep")}
          </p>
        )}
      </div>
    );
  };

  return (
    <section className="studio studio-plan-panel" aria-labelledby={TITLE_ID}>
      <h3 id={TITLE_ID}>{t("studio.plan.title")}</h3>
      {statusRegion}
      {errorBox}

      <dl className="studio-plan-facts">
        <div>
          <dt>{t("studio.plan.facts.plan")}</dt>
          <dd>{tierLabel(t, plan?.tier) || "-"}</dd>
        </div>
        <div>
          <dt>{t("studio.plan.facts.status")}</dt>
          <dd>{planStatusLabel(t, plan, billing)}</dd>
        </div>
        {hasBilling && billing.price && (
          <div>
            <dt>{t("studio.plan.facts.price")}</dt>
            <dd>{priceLine(t, billing.price, locale)}</dd>
          </div>
        )}
        {paid && billing.renewsAt && (
          <div>
            <dt>{ending ? t("studio.plan.facts.endsOn") : t("studio.plan.facts.nextCharge")}</dt>
            <dd>{formatDay(billing.renewsAt, locale)}</dd>
          </div>
        )}
        {hasBilling && billing.country && (
          <div>
            <dt>{t("studio.plan.facts.country")}</dt>
            <dd>{t(`studio.plans.countries.${billing.country}`, { defaultValue: billing.country })}</dd>
          </div>
        )}
      </dl>
      {hasBilling && (
        <p className="studio-muted">
          {billing.managedPayments
            ? t("studio.plan.facts.charged")
            : t("studio.plan.facts.statement", { descriptor: billing.descriptor || "CODENEC" })}
          {paid && !ending && <> · {t("studio.plan.facts.reminders")}</>}
        </p>
      )}

      {banner && <div className="studio-notice"><p>{banner}</p></div>}

      {plan && plan.status !== "none" && <StudioMeter plan={plan} locale={locale} />}

      {billing?.refund && (
        <p className="studio-muted">
          {t("studio.plan.refund", {
            date: formatDay(billing.refund.eligibleUntil, locale),
            max: billing.refund.maxMessages ?? 20,
            used: billing.refund.messagesUsed ?? 0,
          })}
        </p>
      )}

      {billing?.pendingChange && paid && (
        <div className="studio-notice">
          <p>{t("studio.plan.change.scheduled", { date: formatDay(billing.pendingChange.effectiveAt, locale) })}</p>
          <button type="button" className="studio-btn studio-btn-small" onClick={undoChange} disabled={working !== null}>
            {working === "undo" ? t("studio.plan.working") : t("studio.plan.change.undo")}
          </button>
        </div>
      )}

      {changeForm()}

      <div className="studio-plan-panel__actions">
        {canChange && otherTier && (
          <button type="button" className="studio-btn studio-btn-primary" onClick={() => openChange(otherTier)} disabled={working !== null}>
            {working === "preview"
              ? t("studio.plan.change.loading")
              : otherTier === "business"
                ? t("studio.plan.change.upgradeTo")
                : t("studio.plan.change.downgradeTo")}
          </button>
        )}
        {ending && billing.canResume && (
          <button type="button" className="studio-btn studio-btn-primary" onClick={resumePlan} disabled={working !== null}>
            {working === "resume" ? t("studio.plan.working") : t("studio.plan.resume")}
          </button>
        )}
        {(paid || pastDue) && !ending && !confirmingCancel && (
          <button type="button" className="studio-btn" onClick={() => setConfirmingCancel(true)} disabled={working !== null}>
            {t("studio.plan.cancel")}
          </button>
        )}
        {hasBilling && (
          <button type="button" className="studio-btn" onClick={portal} disabled={working !== null}>
            {working === "portal" ? t("studio.plan.opening") : t("studio.plan.manageBilling")}
          </button>
        )}
        {((!hasBilling && plan?.source !== "comp") || plan?.status === "lapsed") && (
          <Link className="studio-btn" to="/studio/plans">{t("studio.plan.seePlans")}</Link>
        )}
      </div>

      {confirmingCancel && (
        <div className="studio-notice" role="group" aria-labelledby="studio-plan-cancel-question">
          <p id="studio-plan-cancel-question" ref={cancelQuestionRef} tabIndex={-1}>
            {pastDue
              ? t("studio.plan.cancelQuestionNow")
              : t("studio.plan.cancelQuestion", { date: formatDay(billing.renewsAt, locale) })}
            {billing.managedPayments && <> {t("studio.plan.cancelLink")}</>}
          </p>
          <div className="studio-plan-panel__actions">
            <button type="button" className="studio-btn studio-btn-primary" onClick={cancelPlan} disabled={working !== null}>
              {working === "cancel" ? t("studio.plan.working") : t("studio.plan.cancelYes")}
            </button>
            <button type="button" className="studio-btn" onClick={() => setConfirmingCancel(false)} disabled={working !== null}>
              {t("studio.plan.cancelNo")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default StudioPlanPanel;
